import { getRequestHeaders, handleRes, logError } from "utils";
import { EntityTypes, Permit, PermitStatus } from "types";
import { SendPermitInviteResponse } from "./properties";
import ReactGA from "react-ga4";

interface GetPermitsByEntityParams {
  tenantID: number | string | undefined;
  entityID: number | string | undefined;
  entityType: EntityTypes;
  includeInactive: boolean;
}
export const getPermitsByEntityID = ({
  tenantID,
  entityID,
  entityType,
  includeInactive,
}: GetPermitsByEntityParams): Promise<Permit[]> => {
  return fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permit?includeInactive=${includeInactive}`,
    {
      method: "GET",
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Permit[]>(res, 200))
    .catch((e) => {
      logError(e);
      return [];
    });
};

interface UpdatePermitParams {
  tenantID: number | string | undefined;
  permitID: number | string | undefined;
  entityType:
    | EntityTypes.Property
    | EntityTypes.PropertyGroup
    | EntityTypes.None;
  entityID: number | string | undefined;
  body: string; // Permit as JSON string
}
export const updatePermit = ({
  tenantID,
  permitID,
  entityType,
  entityID,
  body,
}: UpdatePermitParams): Promise<Response> => {
  return fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permit/${permitID}`,
    {
      method: "PUT",
      ...getRequestHeaders(),
      body,
    },
  );
};
interface RevokePermitParams {
  tenantID: number | string | undefined;
  entityType: EntityTypes;
  entityID: number | string | undefined;
  permitID: number | string | undefined;
  body: string; // Permit as JSON string
}
export const revokePermit = async ({
  tenantID,
  entityType,
  entityID,
  permitID,
  body,
}: RevokePermitParams): Promise<Response> => {
  return await fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permit/${permitID}/revoke`,
    {
      method: "PUT",
      ...getRequestHeaders(),
      body,
    },
  );
};

interface SendPermitInviteParams {
  tenantID: number | string | undefined;
  permitTypeID: number | string | undefined;
  entityID: number | string | undefined;
  entityType:
    | EntityTypes.Property
    | EntityTypes.PropertyGroup
    | EntityTypes.None;
  body: string; // PermitInviteBody as JSON string
}
export const sendPermitInvite = async ({
  tenantID,
  permitTypeID,
  entityID,
  entityType,
  body,
}: SendPermitInviteParams): Promise<SendPermitInviteResponse> => {
  return await fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permitType/${permitTypeID}/invite`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body,
    },
  );
};
interface ResendPermitInviteParams {
  tenantID: number | string | undefined;
  permitTypeID: number | string | undefined;
  entityID: number | string | undefined;
  entityType:
    | EntityTypes.Property
    | EntityTypes.PropertyGroup
    | EntityTypes.None;
  permitID: number | string | undefined;
  body: string; // PermitInviteBody as JSON string
}
export const resendPermitInvite = async ({
  tenantID,
  permitTypeID,
  entityID,
  entityType,
  permitID,
  body,
}: ResendPermitInviteParams): Promise<Response> => {
  return await fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permitType/${permitTypeID}/invite/${permitID}`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body,
    },
  );
};
interface SendSelfServiceLinkParams {
  tenantID: number | string | undefined;
  entityID: number | string | undefined;
  entityType:
    | EntityTypes.Property
    | EntityTypes.PropertyGroup
    | EntityTypes.None;
  body: string; // PermitInviteBody as JSON string
}
export const sendSelfServiceLink = async ({
  tenantID,
  entityID,
  entityType,
  body,
}: SendSelfServiceLinkParams): Promise<Response> => {
  return await fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/sendSelfServiceLink`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body,
    },
  );
};

interface CreatePermitParams {
  tenantID: number | string | undefined;
  permitTypeID: number | string | undefined;
  entityID: number | string | undefined;
  entityType:
    | EntityTypes.Property
    | EntityTypes.PropertyGroup
    | EntityTypes.None;
  body: string; // Permit as JSON string
}
export const createPermit = async ({
  tenantID,
  permitTypeID,
  entityID,
  entityType,
  body,
}: CreatePermitParams): Promise<Response> => {
  return await fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permitType/${permitTypeID}`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body,
    },
  );
};

interface ImportPermitsParams {
  tenantID: number | string | undefined;
  entityType:
    | EntityTypes.Property
    | EntityTypes.PropertyGroup
    | EntityTypes.None;
  entityID: number | string | undefined;
  permitTypeID: number | string | undefined;
  body: string; // Permit as JSON string
}
export const importPermits = async ({
  tenantID,
  entityType,
  entityID,
  permitTypeID,
  body,
}: ImportPermitsParams): Promise<Response> => {
  return await fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permitType/${permitTypeID}/import`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body,
    },
  );
};

interface SaveImportColumnMappingsParams {
  tenantID: number | string | undefined;
  entityID: number | string | undefined;
  entityType: EntityTypes;
  permitTypeID: number | string | undefined;
  body: string; // SavedImportMapping as JSON string
}
export const saveImportColumnMappings = async ({
  tenantID,
  entityID,
  entityType,
  permitTypeID,
  body,
}: SaveImportColumnMappingsParams): Promise<Response> => {
  return await fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permitType/${permitTypeID}/import/columnMappings`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body,
    },
  );
};

export interface PermitReviewParams {
  tenantID: number | string | undefined;
  entityID: number | string | undefined;
  entityType: EntityTypes;
  permitID: number | string | undefined;
  approved: boolean;
}
export const reviewPermit = async ({
  tenantID,
  entityID,
  entityType,
  permitID,
  approved,
}: PermitReviewParams): Promise<Response> => {
  return await fetch(
    `${
      window.PERMIT_BASE_URL
    }/tenant/${tenantID}/${entityType}/${entityID}/permit/${permitID}/${
      approved ? "approve" : "deny"
    }`,
    {
      method: "PUT",
      ...getRequestHeaders(),
    },
  );
};

interface PermitRefundParams {
  tenantID: number | string | undefined;
  entityType: EntityTypes;
  entityID: number | string | undefined;
  permitID: number | string | undefined;
  permitTypeID: number | string | undefined;
  paymentID: number | string | undefined;
  cancel: boolean;
  body: string; // Contact as JSON string (if email is changed)
}
export const refundPermit = async ({
  tenantID,
  permitID,
  entityType,
  entityID,
  permitTypeID,
  paymentID,
  cancel,
  body,
}: PermitRefundParams): Promise<Response> => {
  return await fetch(
    `${
      window.PERMIT_BASE_URL
    }/tenant/${tenantID}/${entityType}/${entityID}/permitType/${permitTypeID}/permit/${permitID}/payment/${paymentID}/${
      cancel ? "cancel" : "refund"
    }`,
    {
      method: "POST",
      ...getRequestHeaders(),
      body,
    },
  );
};

interface GetPermitByIDParams {
  tenantID: number | string | undefined;
  permitID: number | string | undefined;
  entityType: EntityTypes;
  entityID: number | string | undefined;
}
export const getPermitByID = async ({
  tenantID,
  permitID,
  entityType,
  entityID,
}: GetPermitByIDParams): Promise<Permit> => {
  return fetch(
    `${window.PERMIT_BASE_URL}/tenant/${tenantID}/${entityType}/${entityID}/permit/${permitID}`,
    {
      method: "GET",
      ...getRequestHeaders(),
    },
  )
    .then((res) => handleRes<Permit>(res, 200))
    .catch((e) => {
      throw e;
    });
};


